/**
 * Typography configuration for Equine Performance Labs
 * 
 * Production-ready typography system with healthcare-appropriate fonts
 */

export const typography = {
  // Font families
  fontFamily: [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  
  fontFamilyMonospace: [
    'JetBrains Mono',
    'Consolas',
    'Monaco',
    '"Andale Mono"',
    '"Ubuntu Mono"',
    'monospace',
  ].join(','),
  
  // Font weights
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontWeightExtraBold: 800,
  
  // Headings
  h1: {
    fontSize: '3rem', // 48px
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    '@media (max-width:600px)': {
      fontSize: '2.25rem', // 36px
    },
  },
  
  h2: {
    fontSize: '2.25rem', // 36px
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    '@media (max-width:600px)': {
      fontSize: '1.875rem', // 30px
    },
  },
  
  h3: {
    fontSize: '1.875rem', // 30px
    fontWeight: 700,
    lineHeight: 1.4,
    '@media (max-width:600px)': {
      fontSize: '1.5rem', // 24px
    },
  },
  
  h4: {
    fontSize: '1.5rem', // 24px
    fontWeight: 600,
    lineHeight: 1.4,
    '@media (max-width:600px)': {
      fontSize: '1.25rem', // 20px
    },
  },
  
  h5: {
    fontSize: '1.25rem', // 20px
    fontWeight: 600,
    lineHeight: 1.5,
  },
  
  h6: {
    fontSize: '1.125rem', // 18px
    fontWeight: 600,
    lineHeight: 1.5,
  },
  
  // Body text
  body1: {
    fontSize: '1rem', // 16px
    lineHeight: 1.7,
    letterSpacing: '0.01em',
  },
  
  body2: {
    fontSize: '0.875rem', // 14px
    lineHeight: 1.6,
    letterSpacing: '0.01em',
  },
  
  // Specialized text
  subtitle1: {
    fontSize: '1.125rem', // 18px
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  
  subtitle2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '0.00714em',
  },
  
  caption: {
    fontSize: '0.75rem', // 12px
    lineHeight: 1.5,
    letterSpacing: '0.02em',
  },
  
  overline: {
    fontSize: '0.75rem', // 12px
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: 1.5,
  },
  
  button: {
    fontSize: '0.875rem', // 14px
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'none', // Override MUI default uppercase
    lineHeight: 1.75,
  },
  
  // Custom variants for healthcare use cases
  statValue: {
    fontSize: '2.5rem', // 40px
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  
  statLabel: {
    fontSize: '0.875rem', // 14px
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    opacity: 0.7,
  },
  
  tableHeader: {
    fontSize: '0.75rem', // 12px
    fontWeight: 700,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    lineHeight: 1.5,
  },
  
  badge: {
    fontSize: '0.625rem', // 10px
    fontWeight: 700,
    letterSpacing: '0.05em',
  },
  
  code: {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.875rem',
    fontWeight: 400,
  },
};

// Text truncation utilities
export const textTruncate = {
  singleLine: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  
  multiLine: (lines) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
  }),
};

// Healthcare-specific text styles
export const healthcareTextStyles = {
  dosage: {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
  },
  
  patientId: {
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },
  
  alert: {
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: '#ef4444',
  },
  
  success: {
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: '#10b981',
  },
};
